import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  ArrowRight, Check, ChevronDown, Info, Shield, Lock, 
  CalendarDays, 
  Fingerprint, Hash, Key, KeyRound, ShieldCheck 
} from "lucide-react";
import { API_URL } from "../config";

// Reads the JWT saved at login. Adjust the key list to match your login code.
const getAuthToken = () => {
  for (const key of ["token", "access_token", "authToken", "jwt"]) {
    const v = localStorage.getItem(key);
    if (v) return v;
  }
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user.token || user.access_token || null;
  } catch {
    return null;
  }
};
const BORDER_COLORS = { pq: "#7c3aed", hybrid: "#2563eb", classical: "#16a34a" };
const ABOUT_NAMES = { pq: "Dilithium3", hybrid: "Hybrid" };

// "1 Year" -> 1, "2 Years" -> 2, anything else -> 3
const YEARS_BY_PERIOD = { "1 Year": 1, "2 Years": 2 };
const getValidityYears = (period) => YEARS_BY_PERIOD[period] ?? 3;

// Updated to match the step numbering and text from the image exactly
const steps = [
  { label: "Upload", displayNum: 1, type: "completed" },
  { label: "Configure Digital Identity", displayNum: 2, type: "completed" },
  { label: "Create New Digital ID", displayNum: 3, type: "current" },
  { label: "Advanced Options", displayNum: 4, type: "upcoming" },
  { label: "Customize Signature", displayNum: 5, type: "upcoming" },
  { label: "Review & Sign", displayNum: 6, type: "upcoming" }
];

const ID_TYPES = [
  {
    id: "pq",
    title: "Post-Quantum (PQ)",
    description: "Create a Post-Quantum digital identity using quantum-safe algorithms.",
    badge: "Recommended",
    selectedBorder: "border-purple-500 bg-[#FAF9FF]",
    radioBorder: "border-purple-600",
    radioDot: "bg-purple-600",
  },
  {
    id: "hybrid",
    title: "Hybrid (PQ + Classical)",
    description: "Create a hybrid identity combining Post-Quantum and Classical algorithms.",
    badge: null,
    selectedBorder: "border-blue-500 bg-blue-50/30",
    radioBorder: "border-blue-600",
    radioDot: "bg-blue-600",
  },
  {
    id: "classical",
    title: "Classical",
    description: "Create a classical digital identity using traditional algorithms.",
    badge: null,
    selectedBorder: "border-green-500 bg-green-50/30",
    radioBorder: "border-green-600",
    radioDot: "bg-green-600",
  },
];

const ALGORITHM_CONFIGS = {
  pq: {
    algorithms: ["Dilithium3 (Recommended)", "Dilithium2", "Dilithium5"],
    hashAlgorithms: ["SHA3-512 (Recommended)", "SHA3-256", "SHAKE256"],
    securityLevel: "Level 5 (High Security)",
    about: "Dilithium3 is a NIST-selected Post-Quantum Digital Signature Algorithm offering high security with strong performance and small signature size.",
  },
  hybrid: {
    algorithms: ["Dilithium3 + ECDSA (Recommended)", "Dilithium2 + ECDSA"],
    hashAlgorithms: ["SHA3-512 (Recommended)", "SHA256"],
    securityLevel: "Level 4 (Balanced Security)",
    about: "Hybrid mode combines Post-Quantum and Classical algorithms for maximum compatibility and future-proofing.",
  },
  classical: {
    algorithms: ["ECDSA P-256 (Recommended)", "RSA-2048"],
    hashAlgorithms: ["SHA3-512 (Recommended)", "SHA256"],
    securityLevel: "Level 2 (Standard Security)",
    about: "Classical algorithms are industry-standard cryptographic methods used globally.",
  },
};

function Card({ children, className = "" }) {
  return (
    <div className={`bg-white border border-gray-100 rounded-xl shadow-sm ${className}`}>
      {children}
    </div>
  );
}

function SectionTitle({ title, subtitle }) {
  return (
    <div className="mb-3">
      <h2 className="text-base font-bold text-gray-900">{title}</h2>
      {subtitle && <p className="text-sm text-gray-400 mt-1">{subtitle}</p>}
    </div>
  );
}

export default function NewDigital() {
  const navigate = useNavigate();
  const location = useLocation();

  const uploadedFile = location.state?.uploadedFile || null;
  const documentData = location.state?.documentData || null;
  const uploadedFiles = location.state?.uploadedFiles || [];
  const selectedMethod = location.state?.selectedMethod || null;
  const documentId = location.state?.documentId || null;

  const [selectedIdType, setSelectedIdType] = useState("pq");
  const [selectedAlgorithm, setSelectedAlgorithm] = useState("Dilithium3 (Recommended)");
  const [selectedHashAlgorithm, setSelectedHashAlgorithm] = useState("SHA3-512 (Recommended)");
  const [keyGeneration, setKeyGeneration] = useState("generate");

  const [fullName, setFullName] = useState("");
  const [organization, setOrganization] = useState("");
  const [orgUnit, setOrgUnit] = useState("");
  const [country, setCountry] = useState("IN - India");
  const [emailAddress, setEmailAddress] = useState("");
  const [validityPeriod, setValidityPeriod] = useState("1 Year");
  const [validFrom, setValidFrom] = useState(new Date().toISOString().split("T")[0]);
  const [validTill, setValidTill] = useState(() => { const d = new Date(); d.setFullYear(d.getFullYear() + 1); return d.toISOString().split("T")[0]; });
  const [friendlyName, setFriendlyName] = useState("");
  const [loading, setLoading] = useState(false);

  const handleValidFromChange = (e) => {
    const from = e.target.value;
    setValidFrom(from);
    if (from) {
      const date = new Date(from);
      const years = getValidityYears(validityPeriod);
      date.setFullYear(date.getFullYear() + years);
      setValidTill(date.toISOString().split("T")[0]);
    } else {
      setValidTill("");
    }
  };

  const handleValidityPeriodChange = (e) => {
    const period = e.target.value;
    setValidityPeriod(period);
    if (validFrom) {
      const date = new Date(validFrom);
      const years = getValidityYears(period);
      date.setFullYear(date.getFullYear() + years);
      setValidTill(date.toISOString().split("T")[0]);
    }
  };

  const currentConfig = ALGORITHM_CONFIGS[selectedIdType];
  const selectedTypeData = ID_TYPES.find((t) => t.id === selectedIdType);

  const handleNext = async () => {
    try {
      setLoading(true);

      // JWT issued by the backend at login (see services/auth_utils.py)
      const token = getAuthToken();

      if (!token) {
        alert("Your session has expired. Please log in again.");
        navigate("/login");
        return;
      }

      const response = await fetch(`${API_URL}/api/digital-id/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        // userId is not sent: the backend takes the account from the token
        body: JSON.stringify({
          friendlyName,
          selectedIdType,
          selectedAlgorithm,
          selectedHashAlgorithm,
          keyGeneration,
          fullName,
          organization,
          organizationUnit: orgUnit,
          country,
          email: emailAddress,
          validFrom,
          validTill,
        }),
      });

      const result = await response.json().catch(() => ({}));

      if (response.status === 401) {
        // Missing / expired / invalid token
        localStorage.removeItem("token");
        alert(result.error || "Session expired. Please log in again.");
        navigate("/login");
        return;
      }

      if (!response.ok) {
        throw new Error(result.error || result.message || "Request failed");
      }

      alert(result.message);

      navigate("/advanced", {
        state: {
          uploadedFile,
          uploadedFiles,
          documentData,
          selectedMethod,
          documentId,

          identityId: result.identity_id,
          identityName: result.identity_name,
          algorithm: result.algorithm,

          selectedIdType,
          selectedAlgorithm,
          selectedHashAlgorithm,
          keyGeneration,
        },
      });
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full min-h-screen bg-white">
      <div className="max-w-[1400px] mx-auto">
        
        {/* Stepper Header */}
        <div className="flex items-center justify-between mb-2 h-[72px]">
          {steps.map((step, index) => {
            let circleClass = "bg-white border-gray-300 text-gray-500";
            let labelClass = "text-gray-500";
            if (step.type === "completed") {
              circleClass = "bg-green-500 border-green-500 text-white";
              labelClass = "text-green-600";
            } else if (step.type === "current") {
              circleClass = "bg-blue-600 border-blue-600 text-white";
              labelClass = "text-blue-600";
            }

            return (
            <div key={step.label} className="flex-1 flex flex-col items-center relative">
              {index !== steps.length - 1 && (
                <div className={`absolute top-5 left-1/2 w-full h-[2px] ${step.type === "completed" ? "bg-green-500" : "bg-gray-300"}`} />
              )}
              <div
                className={`z-10 w-10 h-10 rounded-full flex items-center justify-center text-xs font-semibold border-2 ${circleClass}`}
              >
                {step.type === "completed" ? <Check size={16} strokeWidth={3} className="text-white" /> : step.displayNum}
              </div>
              <p className={`mt-1 text-sm font-medium text-center hidden sm:block ${labelClass}`}>
                {step.label}
              </p>
            </div>
            );
          })}
        </div>

        {/* Main Layout */}
        <div className="grid grid-cols-12 gap-6 items-stretch">

          {/* LEFT: Card 1 + Summary row, then Identity Details below */}
          <div className="col-span-12 lg:col-span-8 flex flex-col gap-6">
            <div className="grid grid-cols-12 gap-6 items-stretch">
              <div className="col-span-8 flex flex-col">
            <Card className="p-4 flex-1 flex flex-col">
              <SectionTitle title="1. Choose Digital ID Type" subtitle="Select the type of Digital ID you want to create." />
              
              <div className="grid grid-cols-3 gap-3 flex-1">
                {ID_TYPES.map((type) => {
                  const isSelected = selectedIdType === type.id;
                  return (
                    <div
                      key={type.id}
                      onClick={() => {
                        setSelectedIdType(type.id);
                        const cfg = ALGORITHM_CONFIGS[type.id];
                        setSelectedAlgorithm(cfg.algorithms[0]);
                        setSelectedHashAlgorithm(cfg.hashAlgorithms[0]);
                      }}
                      style={isSelected ? { borderColor: BORDER_COLORS[type.id] } : {}}
                      className={`relative p-2.5 rounded-xl border-2 flex flex-col items-center text-center cursor-pointer transition h-full justify-between ${
                        isSelected ? "bg-white" : "border-gray-200 hover:border-gray-300 bg-white"
                      }`}
                    >
                      {/* Custom Top Radio Selection Button */}
                      <div className="absolute top-3 left-3">
                        <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${isSelected ? type.radioBorder : "border-gray-300"}`}>
                          {isSelected && <div className={`w-2 h-2 rounded-full ${type.radioDot}`} />}
                        </div>
                      </div>

                      {/* Visual Identity Shield Box Icons */}
                      <div className="my-auto flex flex-col items-center">
                        <p className={`text-[13px] font-bold text-gray-900 leading-tight mb-2 ${type.id === "hybrid" ? "pl-4" : ""} ${type.id === "classical" ? "-mt-3" : ""}`}>{type.title}</p>
                        <div className="flex items-center justify-center mb-2">
                          {type.id === "classical" ? (
                            <div className="relative flex items-center justify-center">
                              <Shield style={{ color: BORDER_COLORS[type.id] }} size={44} />
                              <Lock className="absolute" style={{ color: BORDER_COLORS[type.id] }} size={18} />
                            </div>
                          ) : (
                            <div className="relative flex items-center justify-center">
                              <Shield style={{ color: BORDER_COLORS[type.id] }} size={44} />
                              <svg className="absolute" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="2.5" style={{ fill: BORDER_COLORS[type.id] }}/>
                                <ellipse cx="12" cy="12" rx="10" ry="4" style={{ stroke: BORDER_COLORS[type.id] }} strokeWidth="1.5" fill="none"/>
                                <ellipse cx="12" cy="12" rx="10" ry="4" style={{ stroke: BORDER_COLORS[type.id] }} strokeWidth="1.5" fill="none" transform="rotate(60 12 12)"/>
                                <ellipse cx="12" cy="12" rx="10" ry="4" style={{ stroke: BORDER_COLORS[type.id] }} strokeWidth="1.5" fill="none" transform="rotate(120 12 12)"/>
                              </svg>
                            </div>
                          )}
                        </div>
                        <p className="text-[11px] text-gray-400 leading-normal px-1 mt-1">{type.description}</p>
                      </div>

                      {/* Absolute bottom layout for the recommended badge */}
                      <div className="h-4 flex items-center justify-center">
                        {type.badge && (
                          <span className="text-[11px] font-bold text-purple-700 bg-purple-50 border border-purple-100 px-1.5 py-0.5 rounded-md">
                            {type.badge}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            </div>
              <div className="col-span-4 flex flex-col">
            {/* Digital ID Summary Card */}
            <Card className="p-4 flex-1">
              <p className="text-xs font-bold text-gray-900 mb-3">Digital ID Summary</p>
              <div className="space-y-4">
                {[
                  { icon: <Shield size={14} />, label: "Type", value: `${selectedTypeData?.title.split(" ")[0]} (PQ)` },
                  { icon: <Fingerprint size={14} />, label: "Algorithm", value: selectedAlgorithm.split(" ")[0] },
                  { icon: <Hash size={14} />, label: "Hash Algorithm", value: selectedHashAlgorithm.split(" ")[0] },
                  { icon: <Key size={14} />, label: "Key Usage", value: "Digital Signature, Non-Repudiation" },
                  { icon: <CalendarDays size={14} />, label: "Validity", value: `${validFrom} - ${validTill}` },
                  { icon: <KeyRound size={14} />, label: "Trust Level", value: "Post-Quantum (PQ)", badge: true },
                ].map(({ icon, label, value, badge }) => (
                  <div key={label} className="grid grid-cols-2 gap-2 text-[12.5px] items-center">
                    <div className="flex items-center gap-1.5 text-gray-400 min-w-0">
                      <span className="flex-shrink-0">{icon}</span>
                      <span className="truncate">{label}</span>
                    </div>
                    {badge
                      ? <span className="text-[11px] font-bold text-green-700 bg-green-50 border border-green-100 px-2 py-0.5 rounded-md">{value}</span>
                      : <span className="font-semibold text-gray-800 text-left leading-tight">{value}</span>
                    }
                  </div>
                ))}
              </div>
            </Card>
            </div>
            </div>

            {/* 2. Identity Details */}
            <Card className="p-4">
              <SectionTitle title="2. Identity Details" subtitle="Enter the details for your new Digital ID." />
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-[13px] font-medium text-gray-400 mb-1">Full Name (CN) <span className="text-red-500">*</span></label>
                  <input value={fullName} onChange={(e) => setFullName(e.target.value)} className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-[13px] bg-[#FCFDFE] text-gray-800 outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-gray-400 mb-1">Organization (O) <span className="text-red-500">*</span></label>
                  <input value={organization} onChange={(e) => setOrganization(e.target.value)} className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-[13px] bg-[#FCFDFE] text-gray-800 outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-gray-400 mb-1">Organizational Unit (OU)</label>
                  <input value={orgUnit} onChange={(e) => setOrgUnit(e.target.value)} className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-[13px] bg-[#FCFDFE] text-gray-800 outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-gray-400 mb-1">Country (C) <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <select value={country} onChange={(e) => setCountry(e.target.value)} className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-[13px] bg-[#FCFDFE] text-gray-800 appearance-none outline-none focus:border-blue-500">
                      <option>IN - India</option>
                    </select>
                    <ChevronDown size={12} className="absolute right-3 top-3 text-gray-400 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-gray-400 mb-1">Email Address</label>
                  <input value={emailAddress} onChange={(e) => setEmailAddress(e.target.value)} className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-[13px] bg-[#FCFDFE] text-gray-800 outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-gray-400 mb-1">Friendly Name</label>
                  <input value={friendlyName} onChange={(e) => setFriendlyName(e.target.value)} className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-[13px] bg-[#FCFDFE] text-gray-800 outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-gray-400 mb-1">Key Usage</label>
                  <div className="relative">
                    <select className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-[13px] bg-[#FCFDFE] text-gray-800 appearance-none outline-none focus:border-blue-500">
                      <option>Digital Signature, Non-Repudiation</option>
                    </select>
                    <ChevronDown size={12} className="absolute right-3 top-3 text-gray-400 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-gray-400 mb-1">Validity Period</label>
                  <div className="relative">
                    <select value={validityPeriod} onChange={handleValidityPeriodChange} className="w-full border border-gray-200 rounded-lg pl-2 pr-5 py-1.5 text-[13px] bg-[#FCFDFE] text-gray-800 appearance-none outline-none focus:border-blue-500">
                      <option>1 Year</option>
                      <option>2 Years</option>
                      <option>3 Years</option>
                    </select>
                    <ChevronDown size={12} className="absolute right-2 top-3 text-gray-400 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-gray-400 mb-1">Valid From</label>
                  <input type="date" value={validFrom} onChange={handleValidFromChange} className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-[13px] bg-[#FCFDFE] text-gray-800 outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-gray-400 mb-1">Valid Till</label>
                  <input type="date" value={validTill} readOnly className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-[13px] bg-gray-50 text-gray-500 outline-none cursor-not-allowed" />
                </div>
              </div>
            </Card>
          </div>

          {/* RIGHT: Cryptographic Configuration */}
          <div className="col-span-12 lg:col-span-4 flex flex-col">
            <Card className="p-3 flex-1">
              <SectionTitle title="3. Cryptographic Configuration" subtitle="Select the algorithm and key parameters." />
              <div className="space-y-3">
                <div>
                  <label className="block text-[13px] font-medium text-gray-500 mb-1">Algorithm Suite</label>
                  <div className="relative">
                    <select
                      value={selectedAlgorithm}
                      onChange={(e) => setSelectedAlgorithm(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg appearance-none bg-[#FCFDFE] text-[13px] focus:outline-none focus:border-blue-500 text-gray-800"
                    >
                      {currentConfig.algorithms.map((algo) => (
                        <option key={algo} value={algo}>{algo}</option>
                      ))}
                    </select>
                    <ChevronDown size={12} className="absolute right-3 top-3 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                <div>
                  <label className="block text-[13px] font-medium text-gray-500 mb-1">Hash Algorithm</label>
                  <div className="relative">
                    <select
                      value={selectedHashAlgorithm}
                      onChange={(e) => setSelectedHashAlgorithm(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg appearance-none bg-[#FCFDFE] text-[13px] focus:outline-none focus:border-blue-500 text-gray-800"
                    >
                      {currentConfig.hashAlgorithms.map((hash) => (
                        <option key={hash} value={hash}>{hash}</option>
                      ))}
                    </select>
                    <ChevronDown size={12} className="absolute right-3 top-3 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                <div>
                  <label className="block text-[13px] font-medium text-gray-500 mb-1.5">Key Size / Security Level</label>
                <div className="flex items-center gap-2">
                  <div className="inline-flex items-center bg-green-50 border border-green-100 rounded-md px-2.5 py-1">
                    <span className="text-[13px] font-semibold text-green-700">{currentConfig.securityLevel}</span>
                  </div>
                  <Info size={13} className="text-blue-600" />
                </div>
                </div>

                {/* About Box Info */}
                <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-3">
                  <div className="flex gap-1.5 items-start">
                    <Info size={13} className="text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[13px] font-bold text-blue-900 mb-1">
                        About {ABOUT_NAMES[selectedIdType] ?? "ECDSA"}
                      </p>
                      <p className="text-[12px] text-blue-700/90 leading-relaxed">{currentConfig.about}</p>
                    </div>
                  </div>
                </div>

                {/* Key Generation */}
                <div className="pt-2">
                  <p className="text-[13px] font-bold text-gray-900 mb-3">Key Generation</p>
                  <div className="space-y-2">
                    {[
                      { id: "genNew", value: "generate", label: "Generate new key pair", desc: "A new private/public key pair will be generated." },
                      { id: "useExist", value: "existing", label: "Use existing key pair", desc: "Import your existing private key." },
                    ].map(({ id, value, label, desc }) => {
                      const active = keyGeneration === value;
                      return (
                        <div
                          key={value}
                          onClick={() => setKeyGeneration(value)}
                          className={`flex items-start gap-2.5 p-3 rounded-xl border-2 cursor-pointer transition ${
                            active ? "border-blue-500 bg-blue-50/40" : "border-gray-200 bg-white hover:border-gray-300"
                          }`}
                        >
                          <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                            active ? "border-blue-600" : "border-gray-300"
                          }`}>
                            {active && <div className="w-2 h-2 rounded-full bg-blue-600" />}
                          </div>
                          <div>
                            <label htmlFor={id} className="text-[13px] font-semibold text-gray-800 block cursor-pointer">{label}</label>
                            <span className="text-[12px] text-gray-400 block mt-0.5">{desc}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Private Key Banner */}
                <div className="border border-purple-100 bg-[#FAF9FF] rounded-xl p-3.5">
                  <div className="flex items-start gap-3">
                    <ShieldCheck size={20} className="text-purple-600 flex-shrink-0 mt-0.5" />
                    <p className="text-[12px] text-gray-400 leading-normal">
                      Private key will be securely stored on your selected device and never leaves your control.
                    </p>
                  </div>
                </div>

              </div>
            </Card>
          </div>

        </div>

        {/* Footer Actions Row */}
        <div className="flex justify-between items-center mt-6 pt-4">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 h-10 px-5 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition text-xs font-semibold shadow-sm"
          >
            Cancel
          </button>
          <button
    onClick={handleNext}
    disabled={loading}
    className={`inline-flex items-center gap-2 h-10 px-6 rounded-lg text-white text-xs font-semibold shadow-sm transition
    ${
        loading
        ? "bg-gray-400 cursor-not-allowed"
        : "bg-blue-700 hover:bg-blue-800"
    }`}
>
    {loading ? "Generating..." : "Generate Digital ID"}

    {!loading && (
        <ArrowRight size={13} strokeWidth={2.5}/>
    )}
</button>
          </div>

      </div>
    </div>
  );
}